import { useState, useEffect, useRef } from 'react';

const ProductPodNodes = Object.freeze({
  PLP_ROOT: '#plp-grid',
  POD_CLASS: '.browse-search__pod',
  NO_PRODUCTS_CLASS: '.results-wrapped--no-products',
  POD_PLACEHOLDER_DATA_TEST_ID: '[data-testid="product-pod-placeholder"]'
});

const getNodeToObserve = ({ podIndexToObserve = null }) => {
  const { PLP_ROOT, POD_CLASS, NO_PRODUCTS_CLASS } = ProductPodNodes;
  const pods = document.querySelectorAll(`${PLP_ROOT} > ${POD_CLASS}`);
  const podLength = pods?.length || 0;
  const productPodTarget = pods?.[podIndexToObserve] || pods?.[podLength - 1];
  return productPodTarget || document.querySelector(NO_PRODUCTS_CLASS);
};

export const useProductPodObserver = () => {
  const [entry, setEntry] = useState({});
  const productPodNodeRef = useRef(null);
  const placeholderPodNodeRef = useRef(null);

  const intersectionObserverRef = useRef(
    typeof (window) !== 'undefined'
    && new window.IntersectionObserver((observedEntries) => {
      const [observedEntry1, observedEntry2] = observedEntries;

      if (observedEntry1?.isIntersecting || observedEntry2?.isIntersecting) {
        setEntry({ isIntersecting: true });

        if (productPodNodeRef.current) {
          intersectionObserverRef.current.unobserve(productPodNodeRef.current);
        }

        if (placeholderPodNodeRef.current) {
          intersectionObserverRef.current.unobserve(placeholderPodNodeRef.current);
        }
      }
    }, { rootMargin: '2000px 0px 2000px 0px' })
  );

  const disconnect = () => {
    if (!intersectionObserverRef?.current) return null;
    intersectionObserverRef.current.disconnect();
    return null;
  };

  const startObserver = (props) => {
    const { podIndexToObserve = null } = props || {};

    if (!intersectionObserverRef?.current) return null;

    disconnect();

    productPodNodeRef.current = getNodeToObserve({ podIndexToObserve });
    if (productPodNodeRef.current) {
      intersectionObserverRef.current.observe(productPodNodeRef.current);
    }

    placeholderPodNodeRef.current = document.querySelector(ProductPodNodes.POD_PLACEHOLDER_DATA_TEST_ID);
    if (productPodNodeRef.current && placeholderPodNodeRef.current) {
      intersectionObserverRef.current.observe(placeholderPodNodeRef.current);
    }
    return null;
  };

  useEffect(() => {
    if (!intersectionObserverRef?.current) return null;
    return () => disconnect();
  }, []);

  return [entry, startObserver];
};
